import Image from 'next/image';
import dynamic from 'next/dynamic';
import { HtmlPttV3 } from '@adapters/prismic/types/plp';
import Heading from '@components/ui/Heading';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';
import { useLocale } from '@hooks/useLocale';
import { addLocaleToUrl } from '@utils/url';

import styles from './PTTThree.module.scss';

const VideoButton = dynamic(() => import('../VideoButton'), {
  ssr: false,
});

interface PTTThreeProps {
  ptt: HtmlPttV3;
}

const PTTThree = ({ ptt }: PTTThreeProps) => {
  const locale = useLocale();
  const {
    logo,
    image,
    title,
    body,
    link,
    linkType,
    videoId,
    videoHeight,
    videoWidth,
  } = ptt?.product_tile || {};

  const handleClick = () => {
    if (typeof window !== 'undefined') {
      trackPromotionEvent({
        eventName: PromotionEventName.selectPromotion,
        page: {
          path: window.location.pathname,
          page_type: determinePageType(),
        },
        interaction: {
          coop_brand: ptt?.product_tile['co-op_brand_attribute'] || null,
          interaction_location: InteractionLocation.PTT,
        },
        ecommerce: {
          creative_name: `link,${ptt?.product_tile?.link?.url || ''}`,
          creative_slot: buildCreativeSlotName({
            sliceIndex: ptt?.position,
            interactionLocation: InteractionLocation.PTT,
            itemIndex: ptt?.treatment,
          }),
          promotion_id: ptt?.product_tile?.title || '',
          promotion_name: ptt?.product_tile?.link?.text || '',
          isVideo: Boolean(ptt?.product_tile?.videoId),
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      <div>
        {logo && logo?.url && logo?.alt && (
          <div className={styles.logoContainer}>
            <Image
              src={logo.url}
              alt={logo.alt}
              fill
              sizes="100vw"
              style={{
                objectFit: 'contain',
                objectPosition: 'left',
              }}
            />
          </div>
        )}
        {title && (
          <Heading semanticLevel="h3" styleLevel="3" className={styles.title}>
            {title}
          </Heading>
        )}
        {body && <p className={styles.body}>{body}</p>}
      </div>
      <div className={styles.bottomContainer}>
        <div className={styles.imageContainer}>
          {image && image.url && image.alt && (
            <Image
              className={styles.image}
              src={image.url}
              alt={image.alt}
              fill
              sizes="(min-width: 1128px) 240px,(min-width: 1000px) 250px,(min-width: 767px) 324px,(min-width: 600px) 191px, (min-width: 327px) 50vw, 100vw"
            />
          )}
        </div>
        {linkType === 'cta' && link && link.text && (
          <div className={styles.cta}>
            <a
              href={addLocaleToUrl(link.url || '', locale)}
              className={styles.ctaLink}
              onClick={handleClick}
            >
              {link.text}
            </a>
          </div>
        )}
        {linkType === 'hyperlink' && link && link.url && (
          <a
            href={addLocaleToUrl(link.url || '', locale)}
            className={styles.link}
            onClick={handleClick}
          >
            {link.text}
          </a>
        )}
        {link?.text && videoId && (
          <VideoButton
            videoId={videoId}
            videoHeight={videoHeight}
            videoWidth={videoWidth}
            text={link.text}
          />
        )}
      </div>
    </div>
  );
};

export default PTTThree;
